// =============================================================================
// FILE: ReadyOperationCardV2.js
// PPE → MES → Ready Operation Card (Operator UX)
//
// PURPOSE:
//   Displays READY operation optimized for touch interaction.
//
// UI UPGRADE (2026-02-26):
//   ✔ Industrial MES tile design
//   ✔ Delay awareness coloring
//   ✔ Gradient START action
//   ✔ Elevated hover interaction
// =============================================================================

import React from "react";

// -----------------------------------------------------------------------------
// Delay Intelligence
// -----------------------------------------------------------------------------
const getDelayStatus = (operation) => {
  if (!operation?.planned_end) return "NORMAL";

  const now = new Date();
  const plannedEnd = new Date(operation.planned_end);

  if (now > plannedEnd) return "DELAYED";

  const diffMinutes = (plannedEnd - now) / 60000;

  if (diffMinutes < 15) return "RISK";

  return "NORMAL";
};

const ReadyOperationCardV2 = ({ operation, onStart }) => {
  const delayStatus = getDelayStatus(operation);

  const borderColor =
    delayStatus === "DELAYED"
      ? "#dc2626"
      : delayStatus === "RISK"
      ? "#f59e0b"
      : "#16a34a";

  const statusLabel =
    delayStatus === "DELAYED"
      ? "🔴 DELAYED"
      : delayStatus === "RISK"
      ? "🟡 AT RISK"
      : "🟢 READY";

  const handleStart = () => {
    onStart(operation.plan_activity_id);
  };

  return (
    <div
      onClick={handleStart}
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: "14px",
        padding: "18px",
        background: "#ffffff",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow =
          "0 10px 22px rgba(0,0,0,0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0px)";
        e.currentTarget.style.boxShadow =
          "0 6px 14px rgba(0,0,0,0.08)";
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0 }}>
          ▶ {operation.operation_name || "Operation"}
        </h3>

        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: borderColor,
          }}
        >
          {statusLabel}
        </span>
      </div>

      {/* WORK ORDER */}
      <div style={{ marginTop: 8, lineHeight: "1.6" }}>
      <div style={{ fontWeight: 500 }}>
        📄 WO: {operation.work_order_no || "-"}
      </div>

      <div style={{ fontSize: 14 }}>
        🧾 Plan: {operation.plan_no || "-"}
      </div>

      <div style={{ fontSize: 14 }}>
        📦 Item: {operation.item_code || "-"}{" "}
        {operation.item_name ? `- ${operation.item_name}` : ""}
      </div>

      <div style={{ fontSize: 14, fontWeight: 600 }}>
        🔢 Qty: {operation.planned_qty ?? "-"}
      </div>
    </div>

      {/* WORKCENTER BADGE */}
      <div
        style={{
          display: "inline-block",
          background: "#f1f5f9",
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: 13,
          marginTop: 4,
        }}
      >
        📍 {operation.workcenter || "-"}
      </div>

      {/* TIME WINDOW */}
      <p
        style={{
          marginTop: 12,
          fontSize: 13,
          color: "#6b7280",
        }}
      >
        ⏱ {operation.planned_start || "--"} →{" "}
        {operation.planned_end || "--"}
      </p>

      {/* START ACTION */}
      <div
        style={{
          marginTop: 14,
          background:
            "linear-gradient(135deg,#16a34a,#22c55e)",
          color: "#fff",
          textAlign: "center",
          padding: "12px",
          borderRadius: "10px",
          fontWeight: 700,
          letterSpacing: "0.4px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
        }}
      >
        ▶ START EXECUTION
      </div>
    </div>
  );
};

export default ReadyOperationCardV2;